import { ReactNode } from "react"
import {
  Button,
  ButtonProps,
  Flex,
  Image,
  Spinner,
  Text,
} from "@chakra-ui/react"
import { SupportedTokenName } from "@mezo/sdk"

import {
  RootState,
  selectHasLoadedWalletBalances,
  selectToken,
  useDappSelector,
} from "#/redux-state"
import { bigIntToHumanReadableFormat } from "#/shared/utils"

import CurrencySymbol from "../CurrencySymbol"

type TokenSelectButtonProps = {
  token: SupportedTokenName
  icon: string
  invertColorOnHover?: boolean
  isCurrentToken: boolean
  withTokenBalance?: boolean
  onChange: () => void
  children?: ReactNode
} & ButtonProps

export default function TokenSelectButton({
  token,
  icon,
  invertColorOnHover,
  isCurrentToken,
  withTokenBalance = false,
  onChange,
  children,
  ...props
}: TokenSelectButtonProps) {
  const hasLoadedWalletBalances = useDappSelector(selectHasLoadedWalletBalances)

  const { tokenBalance, tokenDecimals } = useDappSelector((state: RootState) =>
    selectToken(state, token),
  )

  return (
    <Button
      variant="unstyled"
      w="277px"
      bg={isCurrentToken ? "primary.black.100" : "primary.white.100"}
      onClick={onChange}
      display="flex"
      justifyContent="space-between"
      p={2}
      rounded="8px"
      role="group"
      _hover={{ bg: "primary.black.100", color: "primary.white.100" }}
      color={isCurrentToken ? "primary.white.100" : "primary.black.100"}
      {...props}
    >
      <Flex gap={2} alignItems="center">
        <Flex
          rounded="100%"
          h="32px"
          w="32px"
          alignItems="center"
          justifyContent="center"
        >
          <Image
            src={icon}
            w="32px"
            h="32px"
            filter={`invert(${isCurrentToken && invertColorOnHover ? 1 : 0})`}
            _groupHover={{ filter: `invert(${invertColorOnHover ? 1 : 0})` }}
          />
        </Flex>
        <Text
          color={isCurrentToken ? "primary.white.100" : "primary.black.100"}
          _groupHover={{ color: "primary.white.100" }}
        >
          <CurrencySymbol token={token} />
        </Text>
      </Flex>
      {children}
      {withTokenBalance && (
        <Text
          size="md"
          color="inherit"
          style={{
            textAlign: "right",
            textOverflow: "ellipsis",
            overflow: "hidden",
            width: "140px",
          }}
        >
          {hasLoadedWalletBalances ? (
            bigIntToHumanReadableFormat(tokenBalance, tokenDecimals)
          ) : (
            <Spinner size="xs" />
          )}
        </Text>
      )}
    </Button>
  )
}
